import { Children, cloneElement, forwardRef, isValidElement, useCallback, useMemo, useRef } from 'react';
import DataGrid, { Toolbar, Item } from 'devextreme-react/data-grid';
import { GridLayoutToolbar } from './grid-layout-toolbar';
import { useGridLayout } from './use-grid-layout';
import { rewriteBareHttpStatusMessage } from '~/utils';

// Single seam every Squire grid renders through, so cross-cutting grid behaviour
// (starting with saved grid layouts) is added in one place instead of 30+ call sites.
// Every prop and every declarative child (Column, Paging, Toolbar, Editing, ...) is passed
// straight to the DevExtreme grid.
//
// Saved layouts are strictly opt-in via `layoutKey`. With no key this behaves exactly as the
// original pass-through did: no toolbar item, no state storing, and not one request — which is what
// keeps the grids inside editors, tabs and popups out of the feature.
//
// Not exported through dynamicLoader like most components: that helper returns a plain
// function component, which drops `ref`, and callers rely on `gridRef.current.instance()`.
export const SquireDataGrid = forwardRef(function SquireDataGrid(props, ref) {
  const {
    children,
    layoutKey,
    // DevExtreme only restores columns.visible when the grid has a ColumnChooser, so column
    // visibility is stripped from a saved layout unless the grid says it has one.
    enableColumnChooser = false,
    // Rendered as the right-most toolbar item, to the right of the layout menu. The agreed order is
    // [Add ...] [Layout] [view toggle], and the layout menu therefore has to sit between a page's own
    // buttons and its view toggle. The wrapper cannot tell those apart by inspecting opaque children,
    // so the trailing item is handed over explicitly instead of guessed at.
    toolbarTrailingRender,
    onDataErrorOccurred,
    ...dataGridProps
  } = props;

  // The layout hook needs the grid instance to read state() off it, and the page still needs its own
  // ref for refresh/instance calls. Keep an internal one and forward to whatever the caller passed.
  const innerRef = useRef(null);
  const setGridRef = useCallback(
    (instance) => {
      innerRef.current = instance;

      if (typeof ref === 'function') {
        ref(instance);
      } else if (ref) {
        ref.current = instance;
      }
    },
    [ref]
  );

  const gridLayout = useGridLayout(layoutKey, innerRef, { enableColumnChooser });

  // The store hands the grid whatever the failed response carried, which for anything answering with
  // a bare status is the status name itself — "Bad Request", "Unauthorized". DevExtreme re-reads
  // error.message when it renders the error row, after this runs, so rewriting it in place is what
  // the user ends up reading. Returning the page's own result keeps DevExtreme's stopOnFalse
  // contract, where a handler returning false suppresses the error row.
  const handleDataErrorOccurred = useCallback(
    (e) => {
      if (e?.error) {
        e.error.message = rewriteBareHttpStatusMessage(e.error.message);
      }

      return onDataErrorOccurred?.(e);
    },
    [onDataErrorOccurred]
  );

  const renderLayoutToolbar = useCallback(() => <GridLayoutToolbar gridLayout={gridLayout} />, [gridLayout]);

  // The layout menu is injected into whatever toolbar the page declared rather than replacing it.
  // Declaring a Toolbar suppresses DevExtreme's default items, so a page that lists its own
  // (companies re-declares "groupPanel", for instance) must keep every one of them. Grids that
  // declare no toolbar have no default items to lose — none of them enable a search panel, column
  // chooser, group panel or export — so a toolbar is added for them.
  const gridChildren = useMemo(() => {
    const extraItems = [];

    if (gridLayout.isEnabled) {
      extraItems.push(
        <Item
          key="squire-grid-layout"
          location="after"
          render={renderLayoutToolbar}
        />
      );
    }

    if (toolbarTrailingRender) {
      extraItems.push(
        <Item
          key="squire-grid-toolbar-trailing"
          location="after"
          render={toolbarTrailingRender}
        />
      );
    }

    if (extraItems.length === 0) {
      return children;
    }

    const childArray = Children.toArray(children);
    const toolbarIndex = childArray.findIndex((child) => isValidElement(child) && child.type === Toolbar);

    if (toolbarIndex === -1) {
      return [...childArray, <Toolbar key="squire-grid-layout-toolbar">{extraItems}</Toolbar>];
    }

    const pageToolbar = childArray[toolbarIndex];
    const mergedToolbar = cloneElement(
      pageToolbar,
      undefined,
      ...Children.toArray(pageToolbar.props.children),
      ...extraItems
    );

    return childArray.map((child, index) => (index === toolbarIndex ? mergedToolbar : child));
  }, [children, gridLayout.isEnabled, renderLayoutToolbar, toolbarTrailingRender]);

  // Applied after the caller's props on purpose: `layoutKey` is the opt-in, so when a grid has asked
  // for saved layouts the hook owns state storing. No page sets stateStoring itself today.
  const stateStoringProps = gridLayout.stateStoring ? { stateStoring: gridLayout.stateStoring } : undefined;

  return (
    <DataGrid
      ref={setGridRef}
      {...dataGridProps}
      {...stateStoringProps}
      onDataErrorOccurred={handleDataErrorOccurred}
    >
      {gridChildren}
    </DataGrid>
  );
});
